"use client";

import type { JSX } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Article } from "@/lib/directus";
import { getAssetUrl } from "@/lib/directus";
import { formatRelativeDate, estimateReadTime } from "@/lib/dateUtils";
import { trackArticleClick } from "@/lib/analytics";
import { CategoryBadge } from "./CategoryBadge";

// Helper function to get category color class
function getCategoryColorClass(category?: string): string {
  switch (category) {
    case "Training":
      return "text-blue-600";
    case "Nutrition":
      return "text-green-600";
    case "Lifestyle":
      return "text-purple-600";
    default:
      return "text-gray-400";
  }
}

interface ArticleCardProps {
  article: Article;
  variant?: "featured" | "regular";
  priority?: boolean;
  showRelativeDate?: boolean;
  clickSource?: string;
}

export function ArticleCard({
  article,
  variant = "regular",
  priority = false,
  showRelativeDate = false,
  clickSource = "article_card",
}: ArticleCardProps): JSX.Element {
  const featured = variant === "featured";
  const imageData = typeof article.featured_image === 'object' && article.featured_image ? article.featured_image : null;
  const imageUrl = article.featured_image ? getAssetUrl(article.featured_image) : '';
  // Use publish_date for display (date_updated is only for SEO metadata)
  const publishDate = article.publish_date || article.date_created;
  const excerpt = featured ? article.seo_description : undefined;
  const readTime = featured ? estimateReadTime(article.content) : null;

  return (
    <Link
      href={`/blog/${article.slug}`}
      className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-2xl"
      onClick={() =>
        trackArticleClick(article.slug, article.category ?? "", clickSource)
      }
    >
      <article className="relative isolate overflow-hidden rounded-2xl bg-gray-900 w-full">
        {/* Aspect ratio container to prevent CLS. Featured is taller on mobile
            so the badge/title/excerpt/meta stack has room before switching
            to a wide 16:9 once the card has more horizontal space to work with. */}
        <div
          className={featured ? "relative w-full aspect-[3/4] sm:aspect-[16/9]" : "relative w-full"}
          style={featured ? undefined : { paddingBottom: '56.25%' }}
        >
          <div className="absolute inset-0">
            {imageUrl && (
              <Image
                alt={imageData?.description || imageData?.title || article.title}
                src={imageUrl}
                fill
                className={`absolute inset-0 z-background h-full w-full object-cover transition-[filter] duration-300 ${
                  featured
                    ? "group-hover:brightness-110 group-active:brightness-110"
                    : ""
                }`}
                priority={priority}
                quality={75}
                sizes={
                  featured
                    ? "(max-width: 1024px) 100vw, 66vw"
                    : "(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 50vw, 33vw"
                }
              />
            )}
            <div
              className={`absolute inset-0 z-below bg-gradient-to-t from-gray-900 transition-all duration-300 ${
                featured
                  ? "via-gray-900/40 group-hover:from-gray-900 group-hover:via-gray-900/70 group-active:via-gray-900/70"
                  : "via-gray-900/40 group-hover:via-gray-900/60"
              }`}
            />
            <div className="absolute inset-0 z-below rounded-2xl ring-1 ring-inset ring-gray-900/10" />

            {/* Content overlay */}
            <div className={featured ? "absolute bottom-0 left-0 right-0 p-6 sm:p-8 md:p-10 lg:p-12" : "absolute bottom-0 left-0 right-0 p-6 sm:p-8"}>
              {featured && article.category && (
                <CategoryBadge category={article.category} variant="overlay" className="mb-3" />
              )}
              <h3
                className={
                  featured
                    ? "line-clamp-3 text-2xl font-bold leading-tight text-white transition-all duration-300 group-hover:underline group-hover:decoration-indigo-400/80 group-hover:underline-offset-4 sm:text-3xl md:text-4xl lg:text-h1"
                    : "text-lg font-semibold leading-6 text-white md:text-xl"
                }
              >
                {article.title}
              </h3>
              {featured && excerpt && (
                <p className="mt-3 max-w-2xl text-sm text-gray-300 line-clamp-2 sm:text-base">
                  {excerpt}
                </p>
              )}
              <div
                className={
                  featured
                    ? "mt-2 flex items-center gap-1 text-xs text-gray-400"
                    : "mt-3 flex items-center gap-1.5 text-sm text-gray-300 sm:text-base"
                }
              >
                {!featured && (
                  <>
                    <span className={`font-medium ${getCategoryColorClass(article.category)}`}>
                      {article.category}
                    </span>
                    <span className="text-gray-400">•</span>
                  </>
                )}
                <time dateTime={publishDate} suppressHydrationWarning>
                  {formatRelativeDate(publishDate, { showRelative: showRelativeDate })}
                </time>
                {featured && readTime && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span>{readTime}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
